import {
  all,
  call,
  put,
  select,
  takeLatest,
  takeEvery,
} from 'redux-saga/effects';
import qs from 'qs';
import { request } from '../../common/util/RestClient';
import {
  getAllTickets,
  fetchAllParticipants,
  getTicket,
  fetchAllMessages,
  markTicketAsSeen,
  fetchAllTicketStatuses,
  fetchAllTicketPriorities,
  patchTicket,
  fetchTicketHistory,
  loadMoreTicketHistory,
  fetchTicketsV2Summary,
  postTicketMentions,
  updateTicketsListRefreshTs,
  fetchTicketsByEntityV2,
  loadMoreTicketsByEntityV2,
  createMsgForTicket,
  uploadTicketAttachment,
} from '../routines/VelarisSupportRoutines';
import envConfig from '../../config';
import { convertFilterToVfl } from '../../common/util/velarisFilterLanguageUtils';
import SUCCESS_MESSAGES from '../../common/constants/SuccessMessagesConstants';
import {
  allTicketsFromState,
  selectedTicketFromState,
} from '../selectors/supportSelector';
import { CUSTOM_FILTERS } from '../../common/constants/VelarisSupportConstants';
import constants from '../../common/constants/CommonConstants';
import {
  saveUploadedFile,
  uploadFileToS3WithPreSignedUrl,
} from './fileUploadSaga';

const { CONTACT_LEVEL } = constants;

export const generateUrlForTicketsFetch = (payload) => {
  const convertedFilters = convertFilterToVfl(payload.filters);
  let url = `${envConfig.APIG_URL}ticket-management/tickets`;

  const params = Object.keys(payload).reduce((acc, param) => {
    const value = payload[param];
    if (value) acc[param] = value;
    return acc;
  }, {});

  if (convertedFilters) params.filters = convertedFilters;

  if (Object.keys(params).length) {
    url += `?${qs.stringify(params)}`;
  }
  return url;
};

export function* fetchAllParticipantsSaga() {
  try {
    const fetchParticipantsUrl = `${envConfig.APIG_URL}ticket-management/tickets/requesters`;
    const response = yield call(request, fetchParticipantsUrl);
    yield put(fetchAllParticipants.success(response.data));
  } catch (error) {
    yield put(fetchAllParticipants.failure({ error }));
  } finally {
    yield put(fetchAllParticipants.fulfill());
  }
}

export function* fetchAllTicketsSaga({ payload }) {
  try {
    const url = generateUrlForTicketsFetch(payload);
    const response = yield call(request, url);
    yield put(getAllTickets.success(response.data));
  } catch (error) {
    yield put(getAllTickets.failure({ error }));
  } finally {
    yield put(getAllTickets.fulfill());
  }
}

export function* fetchAllMessagesSaga({ payload }) {
  try {
    const { ticketId } = payload;
    const url = `${envConfig.APIG_URL}ticket-management/tickets/${ticketId}/messages`;
    const response = yield call(request, url);
    yield put(fetchAllMessages.success(response.data));
  } catch (error) {
    yield put(fetchAllMessages.failure({ error }));
  } finally {
    yield put(fetchAllMessages.fulfill());
  }
}

export function* markTicketAsSeenSaga({ payload }) {
  try {
    const { ticketId } = payload;
    const url = `${envConfig.APIG_URL}ticket-management/tickets/${ticketId}/seen`;
    yield call(request, url, { method: 'POST' });
    yield put(markTicketAsSeen.success({ ticketId }));
  } catch (error) {
    yield put(markTicketAsSeen.failure({ error }));
  } finally {
    yield put(markTicketAsSeen.fulfill());
  }
}

export function* getTicketByIdSaga({ payload }) {
  try {
    const { ticketId } = payload;
    const allTicket = yield select(allTicketsFromState);
    let ticket = allTicket.find((t) => t.ticketId === parseInt(ticketId, 10));
    if (!ticket) {
      const url = `${envConfig.APIG_URL}ticket-management/tickets/${ticketId}`;
      const { data } = yield call(request, url);
      ticket = data;
    }
    yield put(getTicket.success(ticket));
  } catch (error) {
    yield put(getTicket.failure({ error }));
  } finally {
    yield put(getTicket.fulfill());
  }
}

export function* fetchAllStatusesSaga() {
  try {
    const fetchStatusesUrl = `${envConfig.APIG_URL}ticket-management/tickets/statuses`;
    const response = yield call(request, fetchStatusesUrl);
    yield put(fetchAllTicketStatuses.success(response.data));
  } catch (error) {
    yield put(fetchAllTicketStatuses.failure({ error }));
  } finally {
    yield put(fetchAllTicketStatuses.fulfill());
  }
}

export function* fetchAllPrioritiesSaga() {
  try {
    const fetchPrioritiesUrl = `${envConfig.APIG_URL}ticket-management/tickets/priorities`;
    const response = yield call(request, fetchPrioritiesUrl);
    yield put(fetchAllTicketPriorities.success(response.data));
  } catch (error) {
    yield put(fetchAllTicketPriorities.failure({ error }));
  } finally {
    yield put(fetchAllTicketPriorities.fulfill());
  }
}

export function* patchTicketSaga({ payload }) {
  try {
    const { ticketId, data, callback } = payload;
    const url = `${envConfig.APIG_URL}ticket-management/tickets/${ticketId}`;
    const existingTicket = yield select(selectedTicketFromState);
    const response = yield call(request, url, {
      method: 'PATCH',
      payload: data,
    });
    yield put(
      patchTicket.success({
        data: response.data,
        successMessage: SUCCESS_MESSAGES.TICKET_UPDATED,
      })
    );
    yield put(fetchTicketHistory.trigger({ ticketId }));
    if (callback) callback(existingTicket, response.data);
  } catch (error) {
    yield put(
      patchTicket.failure({
        error,
        errorMessage: 'Failed to update ticket',
      })
    );
  } finally {
    yield put(patchTicket.fulfill());
  }
}

export function* fetchTicketHistorySaga({ payload }) {
  try {
    const { ticketId } = payload;
    const fetchHistoryUrl = `${envConfig.APIG_URL}ticket-management/tickets/${ticketId}/history`;
    const { data: ticketHistory } = yield call(request, fetchHistoryUrl);
    yield put(fetchTicketHistory.success(ticketHistory));
  } catch (error) {
    yield put(fetchTicketHistory.failure({ error }));
  } finally {
    yield put(fetchTicketHistory.fulfill());
  }
}

export function* loadMoreTicketHistorySaga({ payload }) {
  try {
    const { ticketId, page } = payload;
    const fetchMoreHistoryUrl = `${envConfig.APIG_URL}ticket-management/tickets/${ticketId}/history?page=${page}`;
    const { data: moreHistory } = yield call(request, fetchMoreHistoryUrl);
    yield put(loadMoreTicketHistory.success(moreHistory));
  } catch (error) {
    yield put(loadMoreTicketHistory.failure({ error }));
  } finally {
    yield put(loadMoreTicketHistory.fulfill());
  }
}

export function* fetchTicketSummarySaga({ payload }) {
  try {
    const { level, entityId, days, entityEmail } = payload;
    let queryString;
    if (level !== CONTACT_LEVEL) {
      queryString = `level=${level.toUpperCase()}&entityId=${entityId}`;
    } else {
      queryString = `requester=${encodeURIComponent(entityEmail)}`;
    }
    if (days) {
      queryString += `&days=${days}`;
    }
    const url = `${envConfig.APIG_URL}ticket-management/tickets/summary?${queryString}`;
    const { data: ticketSummary } = yield call(request, url);
    yield put(fetchTicketsV2Summary.success(ticketSummary.data));
  } catch (error) {
    yield put(fetchTicketsV2Summary.failure({ error }));
  } finally {
    yield put(fetchTicketsV2Summary.fulfill());
  }
}

export function* postUserTicketMentionsSaga({ payload }) {
  try {
    const { ticketId, selectedCustomFilter, data } = payload;
    const url = `${envConfig.APIG_URL}ticket-management/tickets/${ticketId}/mentions`;
    const response = yield call(request, url, {
      method: 'POST',
      payload: data,
    });
    yield put(postTicketMentions.success(response));
    if (selectedCustomFilter === CUSTOM_FILTERS.MENTIONED) {
      yield put(updateTicketsListRefreshTs.trigger());
    }
  } catch (error) {
    yield put(postTicketMentions.failure({ error }));
  } finally {
    yield put(postTicketMentions.fulfill());
  }
}

export function* fetchTicketsByEntitySaga({ payload }) {
  try {
    const url = generateUrlForTicketsFetch(payload);
    const response = yield call(request, url);
    yield put(fetchTicketsByEntityV2.success(response.data));
  } catch (error) {
    yield put(fetchTicketsByEntityV2.failure({ error }));
  } finally {
    yield put(fetchTicketsByEntityV2.fulfill());
  }
}

export function* loadMoreTicketsByEntitySaga({ payload }) {
  try {
    const url = generateUrlForTicketsFetch(payload);
    const response = yield call(request, url);
    yield put(loadMoreTicketsByEntityV2.success(response.data));
  } catch (error) {
    yield put(loadMoreTicketsByEntityV2.failure({ error }));
  } finally {
    yield put(loadMoreTicketsByEntityV2.fulfill());
  }
}

export function* createMsgForTicketSaga({ payload }) {
  try {
    const { ticketId, message } = payload;
    const url = `${envConfig.APIG_URL}ticket-management/tickets/${ticketId}/messages`;
    let response = { data: message };
    if (!message.messageId) {
      response = yield call(request, url, {
        method: 'POST',
        payload: message,
      });
    }
    yield put(createMsgForTicket.success(response));
  } catch (error) {
    yield put(createMsgForTicket.failure({ error }));
  } finally {
    yield put(createMsgForTicket.fulfill());
  }
}

export function* uploadTicketAttachmentSaga({ payload }) {
  const { file, onSuccess, onError } = payload;
  try {
    const s3Response = yield uploadFileToS3WithPreSignedUrl(file);
    const fileData = yield saveUploadedFile({
      ...s3Response,
      source: 'system',
    });
    if (onSuccess) onSuccess(fileData);
    yield put(uploadTicketAttachment.success());
  } catch (error) {
    if (onError) onError(error);
    yield put(uploadTicketAttachment.failure({ error }));
  }
}

export default function* root() {
  yield all([
    takeLatest(fetchAllTicketStatuses.TRIGGER, fetchAllStatusesSaga),
    takeLatest(fetchAllTicketPriorities.TRIGGER, fetchAllPrioritiesSaga),
    takeLatest(fetchAllParticipants.TRIGGER, fetchAllParticipantsSaga),
    takeLatest(getAllTickets.TRIGGER, fetchAllTicketsSaga),
    takeLatest(getTicket.TRIGGER, getTicketByIdSaga),
    takeLatest(fetchAllMessages.TRIGGER, fetchAllMessagesSaga),
    takeLatest(markTicketAsSeen.TRIGGER, markTicketAsSeenSaga),
    takeLatest(patchTicket.TRIGGER, patchTicketSaga),
    takeLatest(fetchTicketHistory.TRIGGER, fetchTicketHistorySaga),
    takeLatest(loadMoreTicketHistory.TRIGGER, loadMoreTicketHistorySaga),
    takeLatest(fetchTicketsV2Summary.TRIGGER, fetchTicketSummarySaga),
    takeLatest(postTicketMentions.TRIGGER, postUserTicketMentionsSaga),
    takeLatest(fetchTicketsByEntityV2.TRIGGER, fetchTicketsByEntitySaga),
    takeLatest(loadMoreTicketsByEntityV2.TRIGGER, loadMoreTicketsByEntitySaga),
    takeLatest(createMsgForTicket.TRIGGER, createMsgForTicketSaga),
    takeEvery(uploadTicketAttachment.TRIGGER, uploadTicketAttachmentSaga),
  ]);
}
